import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from '@l2u/web-shared';
import { NavIcon } from '@l2u/web-shared';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '@l2u/web-shared';
import { companyInfoLabel } from '@l2u/web-shared';
import { flattenRegionTree, useTeamRegions } from '../lib/teamRegions.ts';
import { expandTeamRegionIds } from '@l2u/shared/teamRegionScope';
import {
  appBarTitle,
  hideTabBar,
  initials,
  isDetailRoute,
  tabsForRole,
} from '../lib/mobileNav.ts';
import { moreTabBadge, NAV_BADGE_REFRESH, sidebarBadges } from '../lib/pendingWork.ts';
import { usePendingWork } from './usePendingWork.ts';
import { adminQueries } from '../lib/adminQueries.ts';
import {
  navForRole,
  resolveNavLabel,
  isNavRowLocked,
  NAV_LOCKED_CHIP,
  NAV_LOCKED_TITLE,
  type NavGroup,
  type NavRow,
} from '../lib/adminNav.ts';
import { BottomTabBar } from '@l2u/web-shared';
import { MobileAppBar, MobileAppBarProvider } from '@l2u/web-shared';
import {
  BrandMark,
  PRODUCT_NAME,
  ProductLink,
  displayBrandName,
  isVendorBrandName,
} from '@l2u/web-shared';

// The shell's accent and every neutral it used to hard-code now come from the
// token layer in index.css (`--shell-accent`, `--surface-*`, `--border-*`,
// `--text-*`), reached through the Tailwind aliases in tailwind.config.ts:
// `shell-accent`, `surface-ground`, `line`, `ink`, `muted`, `faint`, `ghost`.
//
// The sidebar NAV ROWS, USER AVATAR and sign-out button follow the tenant:
// active row and avatar are `bg-brand-light text-brand-active` (the wash and
// contrast-clamped on-wash text brandTints derives from --brand-primary), row
// and sign-out hover is `bg-brand-hover` (half that wash). For the default brand the active row and avatar resolve to
// exactly the old --shell-accent pair; hover does NOT - it was the neutral
// surface-inset and is now a pale brand wash for every tenant. Only the
// "Powered by" credit stays on --shell-accent, which is still product-owned;
// see the note in index.css.

const GROUP_LABEL: Record<NavGroup, string> = {
  overview: 'Overview',
  operate: 'Operate',
  configure: 'Configure',
  monitor: 'Monitor',
  admin: 'Admin',
};

type GroupedRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; row: NavRow };

function groupNav(rows: NavRow[]): GroupedRow[] {
  const out: GroupedRow[] = [];
  let last: NavGroup | null = null;
  for (const r of rows) {
    if (r.group !== last) {
      out.push({ kind: 'header', key: `h-${r.group}`, label: GROUP_LABEL[r.group] });
      last = r.group;
    }
    out.push({ kind: 'item', key: r.to, row: r });
  }
  return out;
}

/**
 * The "active team region" picker that scopes every operate list.
 *
 * Extracted out of AdminShell so the mobile More screen (More.tsx) can offer
 * the identical control — including the closure filtering and the stale-value
 * reset — instead of a second implementation that could drift. Both instances
 * share one `['team-regions']` query and one context value, so switching in
 * either place is immediately reflected in the other; the reset effect is
 * idempotent, so running it twice is harmless.
 *
 * The caller supplies the surrounding card/padding: the sidebar and the More
 * screen frame it differently.
 */
export function TeamRegionSelect() {
  const { user, activeTeamRegion, setActiveTeamRegion } = useAdminAuth();
  const { branding } = useBranding();
  const regionsQuery = useTeamRegions(!!user);

  const allRegions = regionsQuery.data?.team_regions ?? [];
  const assignedIds = (user?.team_regions ?? []).map((r) => r.id);
  const isRestricted = assignedIds.length > 0;
  // Restricted members choose within their closure (assigned + descendants);
  // unrestricted members see the whole tree.
  const closure = isRestricted ? new Set(expandTeamRegionIds(assignedIds, allRegions)) : null;
  const regionRows = flattenRegionTree(allRegions).filter(
    (row) => closure === null || closure.has(row.region.id),
  );

  // Reset a stale selection (a region that was deleted, or fell outside the
  // user's closure) once the live option list is known.
  useEffect(() => {
    if (!user || activeTeamRegion === null || regionsQuery.data === undefined) return;
    if (!regionRows.some((row) => row.region.id === activeTeamRegion)) setActiveTeamRegion(null);
  }, [user, activeTeamRegion, regionsQuery.data]);

  const regionLabel = companyInfoLabel(branding, 'teamRegion');
  const allLabel = isRestricted
    ? `All my ${regionLabel.toLowerCase()}s`
    : `All ${regionLabel.toLowerCase()}s`;

  // A <select> whose only row is "All regions" renders identically whether the
  // tenant has no regions or the request failed, so both used to look like a
  // working control. Name the state instead. Loading is deliberately excluded
  // so the label doesn't flicker through "none" on every mount.
  const regionsFailed = regionsQuery.isError;
  const regionsEmpty = !regionsFailed && !regionsQuery.isLoading && regionRows.length === 0;
  const regionsUnavailable = regionsFailed || regionsEmpty;
  const placeholderLabel = regionsFailed
    ? `Couldn't load ${regionLabel.toLowerCase()}s`
    : regionsEmpty
      ? `No ${regionLabel.toLowerCase()}s yet`
      : allLabel;

  return (
    <>
      <div className="font-mono-eyebrow pb-1.5 text-[9.5px] tracking-[0.14em] text-faint">
        ACTIVE {regionLabel.toUpperCase()}
      </div>
      <select
        value={activeTeamRegion ?? ''}
        onChange={(e) => setActiveTeamRegion(e.target.value === '' ? null : e.target.value)}
        disabled={regionsUnavailable}
        className={`w-full rounded-control border bg-surface px-3 py-2 text-sm shadow-sm focus:outline-none ${
          regionsFailed
            ? 'border-red-200 text-red-600'
            : 'border-line text-ink-soft focus:border-line-strong'
        } ${regionsUnavailable ? 'cursor-not-allowed opacity-70' : ''}`}
        aria-label={`Active ${regionLabel}`}
      >
        <option value="">{placeholderLabel}</option>
        {regionRows.map(({ region, depth }) => (
          <option key={region.id} value={region.id}>
            {`${'  '.repeat(depth)}${region.name}`}
          </option>
        ))}
      </select>
    </>
  );
}

export function AdminShell() {
  const { user, loading, signOut } = useAdminAuth();
  const { branding } = useBranding();
  const location = useLocation();

  const isRestricted = (user?.team_regions ?? []).length > 0;

  // The red count on the tab bar's Actions tab and the sidebar's Corrective
  // actions row. It lives here rather than inside BottomTabBar, which is shared
  // with the driver portal and must not fetch an admin endpoint to discover it
  // has no queue.
  //
  // Above the early returns below, because a hook cannot sit under one — which is
  // also why the role is re-resolved inside `enabled` instead of reading the
  // `tabs` computed further down.
  const respondedCount = useQuery({
    // The endpoint has no count-only mode, so we fetch the (small) review queue
    // and take its length. The raw list response is what lands in the cache,
    // under the Corrective Actions page's own 'responded' key — the dashboard's
    // review banner and the page itself read the same entry, so the badge, the
    // banner and the list can never disagree.
    ...adminQueries.correctiveActions('responded'),
    select: (res) => res.corrective_actions.length,
    // This shell never remounts, so without a refresh a company's reply only
    // appeared after a full reload.
    ...NAV_BADGE_REFRESH,
    // Keying off the tab set rather than the role keeps the coupling in one
    // place: a tab list without an 'actions' tab never fetches the review queue.
    // Every role has one today. Every width: the tab bar shows it below `lg`,
    // the sidebar row at `lg`+.
    enabled: !!user && tabsForRole(user.role).some((tab) => tab.key === 'actions'),
  });
  // The Audits and More tabs' counts, and the sidebar's Drivers/Assets counts.
  // Same hook-above-the-returns rule.
  const pending = usePendingWork();

  if (loading) return <CenteredSpinner />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const regionLabel = companyInfoLabel(branding, 'teamRegion');
  const brandName = displayBrandName(branding.companyName);
  const rows = groupNav(navForRole(user.role));

  // Mobile chrome (below `lg`): the app bar carries the page title the sidebar
  // and desktop header carry at `lg`+, and the tab bar replaces the sidebar.
  // Everything here is pure pathname derivation — see lib/mobileNav.ts.
  const tabs = tabsForRole(user.role);
  const title = appBarTitle(location.pathname, branding) ?? brandName;
  const showBack = isDetailRoute(location.pathname);
  const showTabBar = !hideTabBar(location.pathname);
  // A stale or failed count is worse than none on a nav badge: it would send
  // someone into an empty review queue. Pass a key only for a known, non-zero
  // number and BottomTabBar renders nothing otherwise.
  const knownResponded = respondedCount.isSuccess ? respondedCount.data : undefined;
  const navBadges = sidebarBadges({
    correctiveActions: knownResponded,
    drivers: pending.reviewDrivers,
    assets: pending.reviewAssets,
  });
  const badges: Record<string, number> = {
    ...(knownResponded && knownResponded > 0 ? { actions: knownResponded } : {}),
    // Assets is a tab below `lg`, so the sidebar's Assets count rides it here.
    // Drivers is a More row instead and reaches the More tab through byPath.
    ...(navBadges['/assets'] ? { assets: navBadges['/assets'] } : {}),
    // BottomTabBar renders nothing for 0, which is also what an unknown count
    // arrives as — see usePendingWork.
    audits: pending.openAudits,
    more: moreTabBadge(pending.byPath, user.role),
  };

  return (
    // MobileAppBarProvider must sit above both the bar and the <Outlet/> so a
    // screen can publish a subtitle into the bar (see MobileAppBar.tsx).
    <MobileAppBarProvider>
      {/* Below `lg` the shell is a fixed-height column so <main> owns the scroll
        and the tab bar can sit at its foot without `position: fixed`. At `lg`+
        height reverts to auto and the window scrolls, exactly as before. */}
      <div className="flex h-dvh overflow-hidden bg-surface-ground lg:h-auto lg:min-h-screen lg:overflow-visible">
        {/* ===== Sidebar (desktop only — the tab bar covers this below `lg`) =====
          `lg:h-dvh` bounds the aside to one viewport instead of letting it
          stretch to the page height — without that bound `flex-1` hands the nav
          all the room it asks for and its `overflow-y-auto` never engages, so a
          long nav pushes the region picker and sign-out below the fold.
          `lg:sticky lg:top-0` then pins it while the window scrolls the main
          column; `sticky` (not `fixed`) keeps the aside in flow so <main> still
          gets its width from the flex row. Every child below is `shrink-0`
          except <nav>, so the menu is the only thing that scrolls. */}
        <aside className="relative hidden w-[260px] shrink-0 flex-col border-r border-line bg-surface lg:sticky lg:top-0 lg:flex lg:h-dvh">
          {/* Product lockup + portal sublabel (the TENANT's logo lives in the top
              bar; this is the product's own identity). The sublabel is the
              design's mono eyebrow — same treatment as the nav group headers
              below, so the sidebar has one voice for labels.
              No longer an <a>: this used to link to locate2u.com. The link to
              the SupplierClear site lives on the "Powered by" credit instead. */}
          <div className="shrink-0 border-b border-line-soft px-5 pb-4 pt-5">
            <BrandMark wordmark={PRODUCT_NAME} />
            <div className="font-mono-eyebrow mt-2.5 text-[10.5px] tracking-[0.14em] text-muted-soft">
              ADMIN PORTAL
            </div>
          </div>

          {/* Nav — the only scrollable region of the sidebar. `min-h-0` defeats
            the automatic minimum size a flex item gets from its content, which
            would otherwise let a long nav push the footer out of the aside. */}
          <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            {rows.map((row) => {
              if (row.kind === 'header') {
                return (
                  <div
                    key={row.key}
                    className="font-mono-eyebrow px-3 pb-1.5 pt-4 text-[9.5px] tracking-[0.14em] text-faint"
                  >
                    {row.label.toUpperCase()}
                  </div>
                );
              }
              const item = row.row;
              // A locked row is a static div, never a NavLink: the path still
              // redirects (pathAllowedForRole is unchanged), so a clickable
              // entry would bounce the reviewer to the dashboard and read as a
              // bug rather than as a restriction.
              if (isNavRowLocked(item, user.role)) {
                return (
                  <div
                    key={row.key}
                    title={NAV_LOCKED_TITLE}
                    aria-disabled="true"
                    className="relative flex cursor-not-allowed items-center gap-3 rounded-control px-3.5 py-2.5 text-[15px] font-medium text-ghost"
                  >
                    <NavIcon name={item.icon} size={20} className="text-ghost" />
                    <span className="flex-1 text-left">{resolveNavLabel(item, branding)}</span>
                    <span className="rounded-full bg-surface-inset px-2 py-0.5 text-[10px] font-semibold text-muted">
                      {NAV_LOCKED_CHIP}
                    </span>
                  </div>
                );
              }
              return (
                <NavLink
                  key={row.key}
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-control px-3.5 py-2.5 text-[15px] transition ${
                      isActive
                        ? 'bg-brand-light font-semibold text-brand-active'
                        : 'font-medium text-muted hover:bg-brand-hover hover:text-brand-active'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <NavIcon
                        name={item.icon}
                        size={20}
                        className={
                          isActive ? 'text-brand-active' : 'text-faint group-hover:text-brand-active'
                        }
                      />
                      <span className="flex-1 text-left">
                        {resolveNavLabel(item, branding)}
                        {(navBadges[item.to] ?? 0) > 0 && (
                          // Spoken as part of the link's name, like More.tsx's rows.
                          <span className="sr-only"> ({navBadges[item.to]} need your action)</span>
                        )}
                      </span>
                      {(navBadges[item.to] ?? 0) > 0 && (
                        <span
                          aria-hidden="true"
                          className="flex h-[20px] min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white"
                        >
                          {navBadges[item.to]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Active team region switcher (scopes the operate lists) */}
          <div className="shrink-0 border-t border-line-soft px-5 pb-1 pt-3">
            <TeamRegionSelect />
          </div>

          {/* Footer: real user + sign out */}
          <div className="shrink-0 border-t border-line-soft px-3 pb-4 pt-3">
            <button
              type="button"
              onClick={() => void signOut()}
              className="flex w-full items-center gap-3 rounded-control p-2 text-left transition hover:bg-brand-hover"
              title={`Sign out ${user.email}`}
            >
              <div className="font-mono-eyebrow flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-light text-[12px] font-bold text-brand-active">
                {initials(user.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-ink">{user.name}</div>
                <div className="truncate text-[11px] text-muted">
                  {user.role === 'admin' ? 'Admin' : 'Reviewer'} ·{' '}
                  {isRestricted
                    ? user.team_regions.map((r) => r.name).join(', ')
                    : `All ${regionLabel}s`}
                </div>
              </div>
              <NavIcon name="log-out" size={16} className="text-faint" />
            </button>
          </div>
        </aside>

        {/* ===== Main: top bar + content ===== */}
        <div className="flex min-w-0 flex-1 flex-col">
          <MobileAppBar hideAt="lg" title={title} showBack={showBack} userName={user.name} />

          {/* Top header bar (desktop only — MobileAppBar stands in below `lg`) */}
          <header className="hidden items-center gap-4 border-b border-line bg-surface px-8 py-4 lg:flex">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={brandName} className="h-7 w-auto" />
            ) : (
              <span className="text-[16px] font-bold tracking-tight text-ink">{brandName}</span>
            )}
            {/* Tenant name, rule, vendor credit — the design canvas's header
                ("Meridian Group · Powered by SupplierClear"). The product name
                links to the product site (ProductLink, new tab).
                Suppressed entirely when the tenant has no branding of its own,
                because brandName then falls back to the product and the header
                reads "SupplierClear | Powered by SupplierClear". Literally the
                same predicate PoweredByFooter uses on the auth screens — this
                was an exact `!==` while that one trimmed and lowercased, so a
                tenant stored as "supplierclear" got the credit suppressed there
                and duplicated here. */}
            {!isVendorBrandName(brandName) && (
              <>
                <div className="h-6 w-px bg-line" />
                <div className="text-[15px] text-muted">
                  Powered by <ProductLink className="font-semibold text-shell-accent" />
                </div>
              </>
            )}
          </header>

          {/* `min-h-0` lets this be the scroll container inside the fixed-height
            mobile column; at `lg`+ the automatic minimum size comes back and
            the window scrolls the page as it always has. `overflow-y-auto`
            makes `<main>` a scrollport at every breakpoint whether or not it
            can actually scroll, and `position: sticky` (the settings header,
            Questionnaires' summary sidebar) resolves against the nearest
            scrollport rather than the viewport — so at `lg`+ it must be
            reverted back to `visible`, or sticky content pins to a container
            that never scrolls instead of the window that does. */}
          <main className="min-h-0 flex-1 overflow-y-auto bg-surface-ground lg:min-h-[auto] lg:overflow-y-visible">
            <div className="px-4 py-4 lg:px-8 lg:py-6">
              <Outlet />
            </div>
          </main>

          {showTabBar && <BottomTabBar hideAt="lg" tabs={tabs} badges={badges} />}
        </div>
      </div>
    </MobileAppBarProvider>
  );
}
